import React, { createContext, useContext, useState, useEffect } from "react";
import {
  getCurrentSubscription,
  upgradeToPro,
  cancelSubscription,
  SubscriptionStatus,
} from "@/services/subscriptionServices";
import { getCurrentClientData } from "@/services/authServices";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";

interface SubscriptionContextType {
  subscription: SubscriptionStatus | null;
  loading: boolean;
  error: string | null;
  isPro: boolean;
  refreshSubscription: () => Promise<void>;
  navigateToUpgrade: () => void;
  upgradeSubscription: () => Promise<void>;
  cancelCurrentSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined
);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const refreshSubscription = async () => {
    try {
      setLoading(true);
      const data = await getCurrentSubscription();
      setSubscription(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to fetch subscription status");
      console.error("Subscription error:", err);
      toast({
        variant: "destructive",
        title: "Subscription Error",
        description: "Failed to fetch your subscription status",
      });
    } finally {
      setLoading(false);
    }
  };

  const upgradeSubscription = async () => {
    try {
      setLoading(true);
      const success = await upgradeToPro();
      if (success) {
        await refreshSubscription();
        toast({
          title: "Upgrade Successful",
          description: "You've been upgraded to the PRO plan!",
        });
      }
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Upgrade Failed",
        description: err.message || "Failed to upgrade your subscription",
      });
    } finally {
      setLoading(false);
    }
  };

  const cancelCurrentSubscription = async () => {
    try {
      setLoading(true);
      const success = await cancelSubscription();
      if (success) {
        await refreshSubscription();
        toast({
          title: "Subscription Cancelled",
          description: "Your PRO subscription has been cancelled",
        });
      }
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Cancellation Failed",
        description: err.message || "Failed to cancel your subscription",
      });
    } finally {
      setLoading(false);
    }
  };

  const navigateToUpgrade = () => {
    navigate("/pricing");
  };

  useEffect(() => {
    const { token } = getCurrentClientData();
    if (!token) {
      setLoading(false);
      return;
    }

    refreshSubscription();
  }, []);

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        loading,
        error,
        isPro: subscription?.isPro || false,
        refreshSubscription,
        navigateToUpgrade,
        upgradeSubscription,
        cancelCurrentSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider"
    );
  }
  return context;
};
